import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useInventoryData } from "@/hooks/useInventoryData";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, Search, Package, MapPin, Edit2, Trash2, Filter, CalendarDays, AlertTriangle } from "lucide-react";
import { GearFormDialog } from "@/components/inventory/GearFormDialog";

const CATEGORIES = ["Audio", "Video", "Lighting", "Staging", "Cables"];
const STATUSES = ["available", "in_use", "reserved"];
const CONDITIONS = ["good", "damaged", "maintenance"];

export default function InventoryPage() {
  const { role } = useOutletContext();
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterAvailability, setFilterAvailability] = useState("all");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Audio", serial_number: "", status: "available", condition: "good", condition_breakdown: null, location: "warehouse", quantity: 1, notes: "" });

  const { gear, loading, handleCreate, handleUpdate, handleDelete } = useInventoryData(dateStart, dateEnd);
  const canEdit = role === "owner" || role === "manager";
  const canDelete = role === "owner";
  const hasDateRange = dateStart && dateEnd;

  const filtered = gear.filter(g => {
    if (filterCategory !== "all" && g.category !== filterCategory) return false;
    if (filterAvailability === "available" && g.available_quantity === 0) return false;
    if (filterAvailability === "booked" && g.available_quantity === g.total_quantity) return false;
    if (filterAvailability === "shortage" && g.available_quantity > 0) return false;
    if (search && !g.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: "", category: "Audio", serial_number: "", status: "available", condition: "good", condition_breakdown: null, location: "warehouse", quantity: 1, notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ name: item.name, category: item.category, serial_number: item.serial_number || "", status: item.status, condition: item.condition, condition_breakdown: item.condition_breakdown || null, location: item.location || "warehouse", quantity: item.quantity || 1, notes: item.notes || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editItem) { await handleUpdate(editItem.gear_id, form); }
      else { await handleCreate(form); }
      setDialogOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const onDelete = async (id) => {
    try { await handleDelete(id); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const conditionColor = (c) => {
    if (c === "good") return "text-emerald-400";
    if (c === "damaged") return "text-red-400";
    return "text-amber-400";
  };

  const conditionBar = (g) => {
    const cb = g.condition_breakdown || { good: g.quantity || 1, maintenance: 0, damaged: 0 };
    const total = g.quantity || 1;
    const goodPct = total > 0 ? (cb.good / total) * 100 : 0;
    const maintPct = total > 0 ? (cb.maintenance / total) * 100 : 0;
    const dmgPct = total > 0 ? (cb.damaged / total) * 100 : 0;
    return (
      <div>
        <div className="w-24 h-1.5 bg-secondary rounded-full overflow-hidden flex">
          {cb.good > 0 && <div className="h-full bg-emerald-500" style={{ width: `${goodPct}%` }} />}
          {cb.maintenance > 0 && <div className="h-full bg-amber-500" style={{ width: `${maintPct}%` }} />}
          {cb.damaged > 0 && <div className="h-full bg-red-500" style={{ width: `${dmgPct}%` }} />}
        </div>
        <div className="flex gap-2 mt-1">
          {cb.good > 0 && <span className="text-xs text-emerald-400 font-semibold">{cb.good} good</span>}
          {cb.maintenance > 0 && <span className="text-xs text-amber-400 font-semibold">{cb.maintenance} maint</span>}
          {cb.damaged > 0 && <span className="text-xs text-red-400 font-semibold">{cb.damaged} dmg</span>}
        </div>
      </div>
    );
  };

  const availabilityBar = (available, usable, total) => {
    const pct = total > 0 ? (available / total) * 100 : 0;
    let color, textColor;
    if (available === 0) { color = "bg-red-500"; textColor = "text-red-400"; }
    else if (available < usable / 2) { color = "bg-amber-500"; textColor = "text-amber-400"; }
    else { color = "bg-emerald-500"; textColor = "text-emerald-400"; }
    return (
      <div className="flex items-center gap-2">
        <div className="w-20 h-1.5 bg-secondary rounded-full overflow-hidden">
          <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`text-xs font-semibold ${textColor}`}>{available} of {usable}</span>
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const shortageCount = gear.filter(g => g.available_quantity === 0 && g.booked_quantity > 0).length;

  return (
    <div data-testid="inventory-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Inventory</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {gear.length} items &middot; {hasDateRange ? `Availability for ${dateStart} — ${dateEnd}` : "Current availability"}
          </p>
        </div>
        {canEdit && (
          <Button data-testid="add-gear-btn" onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-wider rounded-sm">
            <Plus className="w-3.5 h-3.5 mr-2" /> Add Gear
          </Button>
        )}
      </div>

      {/* Date Range Picker */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-primary shrink-0" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Check Availability</span>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="avail-start-date" variant="outline" className="w-44 h-9 text-sm bg-secondary/30 border-border justify-start font-normal">
                    <CalendarDays className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
                    {dateStart || <span className="text-muted-foreground">Pick date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateStart ? new Date(dateStart + "T00:00:00") : undefined}
                    onSelect={(day) => {
                      if (day) {
                        const d = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
                        setDateStart(d);
                        if (!dateEnd || d > dateEnd) setDateEnd(d);
                      }
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="avail-end-date" variant="outline" className="w-44 h-9 text-sm bg-secondary/30 border-border justify-start font-normal">
                    <CalendarDays className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
                    {dateEnd || <span className="text-muted-foreground">Pick date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateEnd ? new Date(dateEnd + "T00:00:00") : undefined}
                    onSelect={(day) => {
                      if (day) {
                        const d = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
                        setDateEnd(d);
                        if (!dateStart || d < dateStart) setDateStart(d);
                      }
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            {hasDateRange && (
              <Button data-testid="clear-dates-btn" variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => { setDateStart(""); setDateEnd(""); }}>
                Clear dates
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Shortage Alert */}
      {shortageCount > 0 && (
        <div data-testid="shortage-alert" className="bg-red-500/10 border border-red-500/30 rounded-sm p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-xs text-red-300">{shortageCount} item{shortageCount > 1 ? 's' : ''} fully booked{hasDateRange ? ' for this date range' : ''}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="gear-search" placeholder="Search gear..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-secondary/30 border-border h-9 text-sm" />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger data-testid="filter-category" className="w-36 h-9 text-sm bg-secondary/30"><Filter className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAvailability} onValueChange={setFilterAvailability}>
          <SelectTrigger data-testid="filter-availability" className="w-36 h-9 text-sm bg-secondary/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Items</SelectItem>
            <SelectItem value="available">Has Availability</SelectItem>
            <SelectItem value="booked">Partially/Fully Booked</SelectItem>
            <SelectItem value="shortage">Fully Booked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-border overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Category</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Availability</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Condition</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider"><MapPin className="w-3 h-3 inline mr-1" />Location / Event</TableHead>
              {canEdit && <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={canEdit ? 6 : 5} className="text-center py-10 text-muted-foreground">No gear found</TableCell></TableRow>
            )}
            {filtered.map(g => (
              <TableRow key={g.gear_id} data-testid={`gear-row-${g.gear_id}`} className="hover:bg-secondary/20">
                <TableCell>
                  <div className="font-medium text-sm">{g.name}</div>
                  {g.notes && <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">{g.notes}</div>}
                </TableCell>
                <TableCell><Badge variant="secondary" className="text-xs">{g.category}</Badge></TableCell>
                <TableCell>
                  {availabilityBar(g.available_quantity, g.usable_quantity ?? g.total_quantity, g.total_quantity)}
                  {g.booked_events && g.booked_events.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {g.booked_events.map((be) => (
                        <div key={be.event_id} className="text-xs text-muted-foreground">
                          {be.quantity}x → <span className="text-primary">{be.name}</span>
                          <span className="text-muted-foreground/60"> ({be.start_date})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {conditionBar(g)}
                </TableCell>
                <TableCell>
                  <div className="text-sm">{g.location || "warehouse"}</div>
                  {g.assigned_event_name && <div className="text-xs text-primary">{g.assigned_event_name}</div>}
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button data-testid={`edit-gear-${g.gear_id}`} variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(g)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      {canDelete && <Button data-testid={`delete-gear-${g.gear_id}`} variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => onDelete(g.gear_id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </Card>

      <GearFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        form={form}
        setForm={setForm}
        editItem={editItem}
        onSave={handleSave}
      />
    </div>
  );
}
